import React, {useState, useEffect, useMemo} from 'react';
import {Button, Modal, Dialogue, addToast, Text} from '@albert-io/atomic';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import type {ClassroomModelV2} from '@albert-io/models';

import GradeSelector, {
  type GradeLevelOption
} from 'client/Classrooms/shared/GradeSelector/GradeSelector.react';
import {useGetGradeLevelsQuery} from 'gql/shared/GradeLevels.queries.generated';

interface PromptGradeLevelModalProps {
  handleResetTimer: () => void;
  handleClearTimer: () => void;
  classroom: ClassroomModelV2;
}

const PromptGradeLevelModal = ({
  handleClearTimer,
  handleResetTimer,
  classroom
}: PromptGradeLevelModalProps) => {
  const [selectedGradeLevels, setSelectedGradeLevels] = useState<GradeLevelOption[]>([]);
  const [hasError, setHasError] = useState(false);

  const {data, error} = useGetGradeLevelsQuery();

  useEffect(() => {
    if (error) {
      addToast({
        color: 'negative',
        title: 'Error',
        message: 'Something went wrong with your request. Please try again.'
      });
    }
  }, [error]);

  // Server-sorted via `PADDED_GRADE_LEVEL_TYPE` — no client-side sort needed.
  const results = data?.gradeLevels?.results;
  const allGradeLevels: GradeLevelOption[] = useMemo(
    () =>
      (results ?? []).map((result) => ({
        getId: () => result.id,
        getGradeLevelType: () => result.gradeLevelType ?? ''
      })),
    [results]
  );

  const onSave = async () => {
    if (selectedGradeLevels.length === 0) {
      setHasError(true);
    } else {
      const gradeLevelIds = selectedGradeLevels.map((gradeLevel) => gradeLevel.getId());
      try {
        await classroom.updateRelationship('grade_levels_v1', gradeLevelIds).save();
        invalidatePartialInterest({resourcePath: ['classroom_v2', classroom.getId()]});
        addToast({
          color: 'positive',
          title: 'Success!',
          message: `Grade levels added to ${classroom.getName()}`
        });
        handleClearTimer();
      } catch {
        addToast({
          color: 'negative',
          title: 'Error',
          message: 'Something went wrong with your request. Please try again.'
        });
      }
    }
  };

  return (
    <Modal ariaLabel='Create new classroom modal' handleClose={handleResetTimer}>
      {({modalContentStyle}) => {
        return (
          <Dialogue inModal hideCloseBtn paddingLevel={0} className={modalContentStyle}>
            <Dialogue.Body>
              <Text as='div' className='border-subtle p-x2 w-full border-b font-bold'>
                Which grades are taught in {classroom.getName()}?
              </Text>
              <div className='border-subtle px-x2 w-full border-b'>
                <GradeSelector
                  setSelectedGradeLevels={(newSelection) => {
                    if (hasError) {
                      setHasError(false);
                    }
                    setSelectedGradeLevels(newSelection);
                  }}
                  selectedGradeLevels={selectedGradeLevels}
                  errorMessage={hasError ? 'Select at least one grade level.' : null}
                  allGradeLevels={allGradeLevels}
                />
              </div>
              <div className='gap-x1 p-x2 flex justify-end'>
                <Button onClick={handleResetTimer} variant='outlined'>
                  Skip For Now
                </Button>
                <Button onClick={onSave} color='primary'>
                  Save Changes
                </Button>
              </div>
            </Dialogue.Body>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

export default PromptGradeLevelModal;
